import React, { useState } from "react";
import { AlertTriangle, CalendarClock, Clock, Loader2, Send, Users, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { describeEnd, describeRecurrence, formatDateOnly, formatInZone, formatUtc } from "../../lib/scheduleTime";
import { SKIP_REASONS, jobIssue, jobOutcome } from "../../lib/pushOutcome";

// Re-exported: other screens import these from here.
export { SKIP_REASONS, jobIssue, jobOutcome };

// Dialogs must stay inside small screens and the phone's safe areas: a
// 16px gutter, and scroll inside instead of overflowing the viewport.
const DIALOG_FIT =
  "w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto";

/** Human message from an axios error (string, FastAPI 422 list, or 409 object). */
export function apiErrorMessage(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msgs = d.map((x) => String(x?.msg || "").replace(/^Value error, /, "")).filter(Boolean);
    if (msgs.length) return msgs.join(" · ");
  }
  if (d && typeof d === "object" && d.message) return d.message;
  if (!e?.response) return "Network problem — nothing was lost. It's safe to try again.";
  return fallback;
}

const STATUS = {
  scheduled: ["Scheduled", "bg-blue-50 text-blue-800 border-blue-200"],
  queued: ["Queued", "bg-amber-50 text-amber-800 border-amber-200"],
  sending: ["Sending", "bg-amber-50 text-amber-800 border-amber-200"],
  sent: ["Sent", "bg-green-50 text-green-800 border-green-200"],
  partial: ["Partially sent", "bg-amber-50 text-amber-900 border-amber-300"],
  failed: ["Failed", "bg-red-50 text-red-800 border-red-200"],
  cancelled: ["Cancelled", "bg-neutral-100 text-neutral-600 border-neutral-200"],
  skipped: ["Not sent", "bg-neutral-100 text-neutral-700 border-neutral-300"],
};

export const ACTIVE_STATUSES = ["queued", "sending"];

export function JobStatusBadge({ job }) {
  const [label, cls] = STATUS[job.status] || [job.status, "bg-neutral-100 text-neutral-700 border-neutral-200"];
  const retrying = job.status === "sending" && job.pending_retry;
  return (
    <span
      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-medium shrink-0 ${cls}`}
      data-testid={`job-status-${job.id}`}
      data-status={job.status}
    >
      {ACTIVE_STATUSES.includes(job.status) && <Loader2 size={10} className="animate-spin" />}
      {retrying ? "Retrying" : label}
    </span>
  );
}

function Row({ label, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 py-2 border-b border-[hsl(var(--kb-border))] last:border-0" data-testid={testid}>
      <dt className="text-xs uppercase tracking-wide text-[hsl(var(--kb-muted))] sm:w-28 shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm min-w-0 break-words">{children}</dd>
    </div>
  );
}

/**
 * Final check before anything is sent or scheduled. Shows the notification
 * as the recipient will see it, who gets it, and exactly when (in the
 * admin's zone and in UTC).
 */
export function ReviewDialog({
  open, onOpenChange, draft, audienceLabel, reach, when, recurring, timeZone,
  deliveryMode, submitting, error, onConfirm,
}) {
  const scheduling = !!when || !!recurring;
  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className={`max-w-lg ${DIALOG_FIT}`} data-testid="push-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {scheduling ? "Review & schedule" : "Review & send"}
          </DialogTitle>
          <DialogDescription>Check everything below — this goes to real customers.</DialogDescription>
        </DialogHeader>

        <div className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-3" data-testid="push-review-preview">
          <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--kb-muted))] mb-1">Notification preview</div>
          <div className="font-medium break-words [overflow-wrap:anywhere]">{draft.title}</div>
          <div className="text-sm mt-0.5 whitespace-pre-line break-words [overflow-wrap:anywhere]">{draft.body}</div>
        </div>

        <dl>
          <Row label="Audience" testid="push-review-audience">{audienceLabel}</Row>
          <Row label="Reach" testid="push-review-reach">
            {reach > 0 ? `${reach} ${reach === 1 ? "person" : "people"} right now` : (
              <span className="text-amber-700 inline-flex items-start gap-1">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                Nobody matches right now{scheduling ? " — the audience is re-checked at send time." : "."}
              </span>
            )}
          </Row>
          <Row label="When" testid="push-review-when">
            {recurring ? (
              <>
                <div className="font-medium" data-testid="push-review-rule">{describeRecurrence(recurring.rule)}</div>
                <div className="text-xs text-[hsl(var(--kb-muted))]">Time zone: {timeZone}</div>
                <div className="text-xs mt-1">Starts {formatDateOnly(recurring.rule.start_date)} · {describeEnd(recurring.rule)}</div>
                <div className="text-xs mt-1" data-testid="push-review-first">
                  First send: <b>{formatInZone(recurring.preview.first_run_at, timeZone)}</b>
                </div>
                {recurring.preview.next_runs.length > 1 && (
                  <div className="text-xs text-[hsl(var(--kb-muted))]">
                    Then: {recurring.preview.next_runs.slice(1).map((r) => formatInZone(r, timeZone)).join(" · ")}
                  </div>
                )}
              </>
            ) : scheduling ? (
              <>
                <div className="font-medium">{formatInZone(when.iso, timeZone)}</div>
                <div className="text-xs text-[hsl(var(--kb-muted))]">{timeZone} · {formatUtc(when.iso)}</div>
                {when.notice && <div className="text-xs text-amber-700 mt-1">{when.notice}</div>}
              </>
            ) : "Immediately"}
          </Row>
          <Row label="Link">{draft.url || "/"}</Row>
          <Row label="Channels">Web push + mobile app (respects each person's notification settings)</Row>
        </dl>

        {deliveryMode === "sandbox" && (
          <div className="text-xs border border-amber-300 bg-amber-50 text-amber-900 p-2" data-testid="push-review-sandbox">
            Sandbox delivery mode: nothing will reach real devices.
          </div>
        )}
        {error && (
          <div className="text-sm border border-red-200 bg-red-50 text-red-700 p-2 flex gap-2" data-testid="push-review-error" role="alert">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button" className="kb-btn-outline w-full sm:w-auto" disabled={submitting}
            onClick={() => onOpenChange(false)} data-testid="push-review-back">
            Back to edit
          </button>
          <button type="button" className="kb-btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            disabled={submitting} onClick={onConfirm} data-testid="push-review-confirm">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : scheduling ? <CalendarClock size={14} /> : <Send size={14} />}
            {submitting ? (scheduling ? "Scheduling…" : "Sending…")
              : recurring ? "Schedule recurring" : scheduling ? "Schedule notification" : "Send now"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduledItem({ job, audienceLabel, onAskCancel, cancelling }) {
  const outcome = jobOutcome(job);
  return (
    <li className="border border-[hsl(var(--kb-border))] p-3 text-sm" data-testid={`scheduled-item-${job.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium min-w-0 break-words [overflow-wrap:anywhere]">{job.title}</div>
        <JobStatusBadge job={job} />
      </div>
      <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5 line-clamp-2 break-words [overflow-wrap:anywhere]">{job.body}</div>
      <div className="flex items-start gap-1.5 mt-2 text-xs" data-testid={`scheduled-when-${job.id}`}>
        <Clock size={12} className="mt-0.5 shrink-0" />
        <span className="min-w-0">
          {formatInZone(job.scheduled_for, job.timezone)}
          {job.timezone && <span className="text-[hsl(var(--kb-muted))]"> · {job.timezone}</span>}
          {job.series_id && <span className="ml-1 bg-blue-50 text-blue-800 px-1.5 py-0.5" data-testid={`scheduled-recurring-tag-${job.id}`}>Recurring</span>}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-1 text-xs text-[hsl(var(--kb-muted))]">
        <Users size={12} className="shrink-0" /> <span className="truncate">{audienceLabel(job)}</span>
      </div>
      {outcome && <div className="text-xs mt-1 break-words [overflow-wrap:anywhere]" data-testid={`scheduled-outcome-${job.id}`}>{outcome}</div>}
      {jobIssue(job) && <div className="text-xs mt-1 text-red-700 break-words [overflow-wrap:anywhere]" data-testid={`scheduled-issue-${job.id}`}>{jobIssue(job)}</div>}
      {job.status === "scheduled" && (
        <button type="button" onClick={() => onAskCancel(job)} disabled={cancelling}
          className="kb-btn-outline mt-3 inline-flex items-center justify-center gap-1.5 text-xs !py-2 w-full sm:w-auto min-h-[40px]"
          data-testid={`scheduled-cancel-${job.id}`}>
          {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
          {cancelling ? "Cancelling…" : "Cancel"}
        </button>
      )}
    </li>
  );
}

/** Upcoming + past scheduled notifications with a confirmed Cancel action. */
export function ScheduledList({ items, loading, error, audienceLabel, onCancel, cancellingId, recurring }) {
  const [confirming, setConfirming] = useState(null);
  const byTime = (a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for);
  const upcoming = items.filter((j) => ["scheduled", "queued", "sending"].includes(j.status)).sort(byTime);
  const past = items.filter((j) => !["scheduled", "queued", "sending"].includes(j.status)).sort((a, b) => byTime(b, a));

  const section = (title, list, testid, empty) => (
    <div data-testid={testid}>
      <h3 className="text-xs uppercase tracking-wide text-[hsl(var(--kb-muted))] mb-2">{title} ({list.length})</h3>
      {list.length === 0 ? <div className="text-sm text-[hsl(var(--kb-muted))]">{empty}</div> : (
        <ul className="space-y-3">
          {list.map((j) => (
            <ScheduledItem key={j.id} job={j} audienceLabel={audienceLabel}
              onAskCancel={setConfirming} cancelling={cancellingId === j.id} />
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4 sm:p-6" data-testid="scheduled-list">
      <h2 className="font-display text-xl mb-4 flex items-center gap-2"><CalendarClock size={18} /> Scheduled notifications</h2>
      {error && (
        <div className="text-sm border border-red-200 bg-red-50 text-red-700 p-2 mb-3" data-testid="scheduled-load-error">{error}</div>
      )}
      {loading ? (
        <div className="space-y-2" data-testid="scheduled-loading">
          {[0, 1].map((i) => <div key={i} className="h-20 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-6 max-h-[640px] overflow-y-auto pr-1">
          {recurring}
          {section("Upcoming", upcoming, "scheduled-upcoming", "Nothing scheduled.")}
          {section("Past", past, "scheduled-past", "No past scheduled notifications.")}
        </div>
      )}

      <AlertDialog open={!!confirming} onOpenChange={(v) => !v && setConfirming(null)}>
        <AlertDialogContent className={`max-w-md ${DIALOG_FIT}`} data-testid="scheduled-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this scheduled notification?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirming?.title}” will not be sent. This can't be undone — you can schedule it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="w-full sm:w-auto mt-0" data-testid="scheduled-cancel-keep">Keep it</AlertDialogCancel>
            <AlertDialogAction className="w-full sm:w-auto" data-testid="scheduled-cancel-confirm"
              onClick={() => { const j = confirming; setConfirming(null); onCancel(j); }}>
              Cancel notification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
